import { dateTimeFormat, convertTimeZone, todayWithSystemTimeZone } from '@magicdoorinc/date';
import { emptyPlaceholder } from './constant';
import type { ConfigType, Dayjs } from '@magicdoorinc/date';
import type { TFunction } from '@magicdoorinc/ui';

export const windowsToIana = (windowsZone: string) => {
  const mapping: Record<string, string> = {
    'Pacific Standard Time': 'America/Los_Angeles',
    'Mountain Standard Time': 'America/Denver',
    'Central Standard Time': 'America/Chicago',
    'Eastern Standard Time': 'America/New_York',
    // other mappings can be added here
  };
  return mapping[windowsZone] || windowsZone;
};

export function formatAgo(time: ConfigType, num = 1) {
  const units = [
    { ms: 365 * 24 * 60 * 60 * 1000, singular: 'year', plural: 'years' },
    { ms: 30 * 24 * 60 * 60 * 1000, singular: 'month', plural: 'months' },
    { ms: 24 * 60 * 60 * 1000, singular: 'day', plural: 'days' },
    { ms: 60 * 60 * 1000, singular: 'hour', plural: 'hours' },
    { ms: 60 * 1000, singular: 'minute', plural: 'minutes' },
  ];

  const now = todayWithSystemTimeZone();
  const timeDayjs = convertTimeZone(time);
  let timeDiff = now.diff(timeDayjs);

  if (timeDiff < 60 * 1000) {
    return 'just now';
  }

  const parts = [];
  for (let i = 0; i < units.length; i++) {
    if (timeDiff < units[i].ms) continue;
    const count = Math.floor(timeDiff / units[i].ms);
    timeDiff -= count * units[i].ms;
    parts.push(`${count} ${count > 1 ? units[i].plural : units[i].singular}`);
    if (parts.length >= num) break;
  }

  return parts.join(' ') + ' ago';
}

export function getDateRange(dataNow: Dayjs, intervalDays: number, bolPastTime: boolean): Array<string> {
  const oneDayTime = 24 * 60 * 60 * 1000;
  const list = [];
  let lastDay;

  if (bolPastTime === true) {
    lastDay = convertTimeZone(dataNow.valueOf() - intervalDays * oneDayTime);
    list.push(dateTimeFormat(lastDay, { timeFormat: false }));
    list.push(dateTimeFormat(dataNow, { timeFormat: false }));
  } else {
    lastDay = convertTimeZone(dataNow.valueOf() + intervalDays * oneDayTime);
    list.push(dateTimeFormat(dataNow, { timeFormat: false }));
    list.push(dateTimeFormat(lastDay, { timeFormat: false }));
  }

  return list;
}

export function checkAuditTime(startTime: Date | string, endTime: Date | string, nowTime?: Date | string) {
  const date = nowTime ? convertTimeZone(nowTime) : todayWithSystemTimeZone();

  let startDate = convertTimeZone(startTime).valueOf();

  let endDate = convertTimeZone(endTime).valueOf();
  const nowDate = date.valueOf();
  const s = startDate > endDate;

  if (s) [startDate, endDate] = [endDate, startDate];

  if (nowDate > startDate && nowDate < endDate) {
    return s ? false : true;
  } else {
    return s ? true : false;
  }
}

export function diffDates(endDate: string | null | undefined, startDate: string | null | undefined, opts?: { withoutSuffix?: boolean }) {
  if (!endDate && !startDate) {
    return emptyPlaceholder;
  }
  return convertTimeZone(startDate).from(convertTimeZone(endDate), opts?.withoutSuffix);
}

export function isToday(date: ConfigType) {
  const targetDate = convertTimeZone(date);
  const today = todayWithSystemTimeZone();

  return targetDate.isSame(today, 'day');
}

export function isFuture(date: ConfigType) {
  const targetDate = convertTimeZone(date);
  return targetDate.isAfter(todayWithSystemTimeZone());
}

export function formatChatDate(isoDateString: string, t: TFunction) {
  const { $date: dateStr, $time: timeStr } = dateTimeFormat(isoDateString, { timeFormat: 'h:mm A z', returnType: 'object' });

  if (isToday(isoDateString)) {
    return timeStr;
  }

  if (convertTimeZone(isoDateString).isSame(todayWithSystemTimeZone().subtract(1, 'day'), 'day')) {
    return `${t('yesterday at')} ${timeStr}`;
  }

  return `${dateStr} at ${timeStr}`;
}

export function getOrdinalDay(day: number): string {
  if ([1, 21, 31].includes(day)) {
    return `${day}st`;
  } else if ([2, 22].includes(day)) {
    return `${day}nd`;
  } else if ([3, 23].includes(day)) {
    return `${day}rd`;
  } else {
    return `${day}th`;
  }
}

export const convertTo12HourFormat = (time24: string) => {
  const [hours, minutes] = time24.split(':');
  let hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}${minutes !== '00' ? `:${minutes}` : ''} ${ampm}`;
};

export const getCurrentMonthRange = () => {
  const today = todayWithSystemTimeZone();
  const startOfMonth = today.startOf('month');
  const endOfMonth = today.endOf('month');
  return {
    start: startOfMonth.format('YYYY-MM-DD'),
    end: endOfMonth.format('YYYY-MM-DD'),
  };
};

export function diffTime(start: string, end: string, unit: 'hour' | 'minute') {
  const today = todayWithSystemTimeZone().format('YYYY-MM-DD');
  return convertTimeZone(`${today} ${end}`).diff(convertTimeZone(`${today} ${start}`), unit);
}

const durationUnits: Record<string, string> = {
  y: 'year',
  M: 'month',
  d: 'day',
  h: 'hour',
  m: 'minute',
  s: 'second',
};

export function formatDuration(duration: string, t: TFunction) {
  if (!duration) return duration;

  return duration.replace(/(\d+)\s*([yMdhms])/g, (_, value, unit) => {
    const unitName = durationUnits[unit];
    if (!unitName) return `${value}${unit}`;
    const num = parseInt(value, 10);
    const key = num > 1 ? `${unitName}s` : unitName;
    return `${value} ${t(key)}`;
  });
}
